<template>
  <div class="container my-5">
    <div class="card mb-4">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col d-flex align-items-center">
            <h2 class="card-title text-truncate mb-0">{{ lead.first_name }} {{ lead.surname }}</h2>
          </div>
          <div class="col-auto">
            <button
              type="button"
              class="btn btn-sm btn-link"
              v-c-modal:editModal="{}"
            >
              <i class="far fa-pencil-alt"></i>
              Edit
            </button>
          </div>
        </div>
        <div class="row">
          <div v-if="lead.company" class="col-sm-6 col-lg">
            <p>
              <span class="data-label">Company</span>
              {{ lead.company }}
            </p>
          </div>
          <div class="col-sm-6 col-lg">
            <p class="text-truncate">
              <span class="data-label">Email</span>
              {{ lead.email }}
            </p>
          </div>
          <div v-if="lead.phone" class="col-sm-6 col-lg">
            <p>
              <span class="data-label">Phone</span>
              {{ lead.phone }}
            </p>
          </div>
          <div v-if="lead.mobile" class="col-sm-6 col-lg">
            <p>
              <span class="data-label">Mobile</span>
              {{ lead.mobile }}
            </p>
          </div>
          <div v-if="lead.lead_type" class="col-sm-6 col-lg">
            <p>
              <span class="data-label">Looking for</span>
              {{ lead.lead_type }}
            </p>
          </div>
          <div class="col-sm-6 col-lg">
            <p>
              <span class="data-label">Received</span>
              {{ lead.created_at | moment('DD/MM/YYYY') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h2 class="card-title mb-3">Stage</h2>
        <div class="row align-items-end">
          <div class="col-sm-6 col-lg-3 mb-3">
            <label class="control-label">Current stage</label>
            <select class="form-select" :value="lead.stage" @change="updateStage($event.target.value)">
              <option v-for="stage in stages" :key="stage.key" :value="stage.key">{{ stage.name }}</option>
            </select>
          </div>
          <div v-if="lead.stage === 'won'" class="col-sm-6 col-lg-3 mb-3">
            <label class="control-label">Estimated value</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                v-model="value"
                @keyup.enter="saveValue"
              />
              <c-button class="btn btn-outline-primary" @click="saveValue">Save</c-button>
            </div>
          </div>
        </div>
        <div class="row">
          <div v-for="stage in datedStages" :key="stage.key" class="col-sm-6 col-lg-3">
            <p class="mb-2">
              <span class="data-label">{{ stage.name }}</span>
              <template v-if="lead[stage.dateField]">{{ lead[stage.dateField] | moment('DD/MM/YYYY') }}</template>
              <span v-else class="text-muted">&mdash;</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col d-flex align-items-center">
            <h2 class="card-title mb-0">Source</h2>
          </div>
          <div v-if="hasSource" class="col-auto">
            <button type="button" class="btn btn-sm btn-link" @click="showRawSource = !showRawSource">
              {{ showRawSource ? 'Hide raw data' : 'Show raw data' }}
            </button>
          </div>
        </div>

        <p v-if="!hasSource" class="text-muted mb-0">No source data was recorded for this lead.</p>

        <template v-else>
          <div class="row">
            <div class="col-sm-6 col-lg-3">
              <p>
                <span class="data-label">Channel</span>
                {{ channelName(source.channel) || '—' }}
              </p>
            </div>
            <div v-if="source.ads_campaign_id" class="col-sm-6 col-lg-3">
              <p>
                <span class="data-label">Ads campaign</span>
                {{ source.ads_campaign_id }}
              </p>
            </div>
            <div v-if="source.utm_campaign" class="col-sm-6 col-lg-3">
              <p>
                <span class="data-label">UTM campaign</span>
                {{ source.utm_campaign }}
              </p>
            </div>
            <div class="col-sm-6 col-lg-3">
              <p>
                <span class="data-label">Google click ID</span>
                {{ source.gclid ? 'Yes' : 'No' }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6">
              <p class="text-truncate">
                <span class="data-label">Landing page</span>
                <a v-if="source.landing_page" :href="source.landing_page" target="_blank" rel="noopener">{{ source.landing_page }}</a>
                <span v-else>&mdash;</span>
              </p>
            </div>
            <div class="col-sm-6">
              <p class="text-truncate">
                <span class="data-label">Referrer</span>
                <a v-if="source.referrer" :href="source.referrer" target="_blank" rel="noopener">{{ source.referrer }}</a>
                <span v-else>&mdash;</span>
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 col-lg-3">
              <p class="mb-0">
                <span class="data-label">First seen</span>
                <template v-if="source.first_seen_at">{{ source.first_seen_at | moment('DD/MM/YYYY HH:mm') }}</template>
                <span v-else>&mdash;</span>
              </p>
            </div>
            <div v-if="source.submitted_from" class="col-sm-6 col-lg-3">
              <p class="text-truncate mb-0">
                <span class="data-label">Submitted from</span>
                {{ source.submitted_from }}
              </p>
            </div>
          </div>

          <pre v-if="showRawSource" class="lead-source-raw mt-3 mb-0">{{ rawSource }}</pre>
        </template>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h2 class="card-title mb-3">Message</h2>
        <p class="lead-message mb-0">{{ lead.message }}</p>
      </div>
    </div>

    <a href="#" class="text-danger" @click.prevent="confirmArchive">Archive</a>

    <lead-modal ref="editModal" title="Edit Lead" :lead="lead" />
  </div>
</template>

<script>
import { notify } from 'utils'
import { mapState, mapActions } from 'vuex'
import LeadModal from './LeadModal'
import { stages, channelName } from './stages'

export default {
  components: {
    LeadModal
  },

  data () {
    return {
      stages,
      showRawSource: false,
      value: null
    }
  },

  computed: {
    ...mapState('lead', {
      lead: 'single'
    }),

    datedStages () {
      return stages.filter((stage) => stage.dateField)
    },

    source () {
      return this.lead.source || {}
    },

    hasSource () {
      return Object.keys(this.source).length > 0
    },

    rawSource () {
      return JSON.stringify(this.source, null, 2)
    }
  },

  watch: {
    'lead.value': {
      handler (value) {
        this.value = value
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('lead', { deleteSingle: 'deleteSingle', updateSingle: 'updateSingle' }),

    channelName,

    async updateStage (stage) {
      try {
        await this.updateSingle({ id: this.lead.id, payload: { stage } })
        notify.success('Lead stage updated.')
      } catch (error) {
        notify.error('Failed to update the lead stage, please try again.')
      }
    },

    async saveValue () {
      try {
        await this.updateSingle({ id: this.lead.id, payload: { value: this.value === '' ? null : this.value } })
        notify.success('Lead value saved.')
      } catch (error) {
        notify.error('Failed to save the lead value, please try again.')
      }
    },

    confirmArchive () {
      this.$swal({
        title: 'Confirm Archive',
        html: 'Are you sure you want to archive this lead',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Archive'
      }).then(() => {
        this.deleteSingle(this.lead.id).then(() => this.$router.push({ name: 'leads.index' }))
      }, () => {})
    }
  }
}
</script>

<style scoped>
.lead-message {
  white-space: pre-wrap;
}

.lead-source-raw {
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  white-space: pre-wrap;
  word-break: break-all;
  background-color: var(--bs-tertiary-bg, #f8f9fa);
  border-radius: var(--bs-border-radius, 0.375rem);
}
</style>
